import { useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '..';
import { getConfig } from '../config';
import { clearUserData } from 'utils/cleardata';

const useApiEndpoint = (apiUrl) => {
  let axiosInstance = useRef();
  const isAuthenticated = useIsAuthenticated();
  useEffect(() => {
    axiosInstance.current = axios.create({
      baseURL: apiUrl
    });
    axiosInstance.current.interceptors.request.use(
      async (config) => {
        const accounts = msalInstance.getAllAccounts();
        if (accounts?.[0] == null) {
          clearUserData();
          return msalInstance.logoutRedirect({
            postLogoutRedirectUri: '/'
          });
        }
        const account = msalInstance.getAccountByLocalId(accounts[0].localAccountId ?? null);
        const request = {
          scopes: [getConfig().msalConfig.auth.clientId],
          account: account
        };
        return msalInstance.acquireTokenSilent(request).then((response) => {
          config.headers = {
            authorization: `Bearer ${response.idToken}`
          };
          return config;
        }).catch((e) => {
          clearUserData();
          if (e instanceof InteractionRequiredAuthError) {
            msalInstance.acquireTokenRedirect(request);
          }
        });
      },
      (error) => Promise.reject(error)
    );
  }, [apiUrl]);

  const createRequestConfig = useCallback(async (method, path, body) => {
    const cancelTokenSource = axios.CancelToken.source();

    return {
      method: method,
      url: path,
      data: body,
      cancelToken: cancelTokenSource.token
    };
  }, []);

  const request = useCallback(async (method, path, body) => {

    if (!axiosInstance.current || !isAuthenticated) {
      return;
    }

    const requestConfig = await createRequestConfig(method, path, body);
    return axiosInstance.current.request(requestConfig);

  }, [createRequestConfig, isAuthenticated]);

  const get = useCallback(async (path) => {
    return request('get', path);
  }, [request]);

  const post = useCallback(async (path, body) => {
    return request('post', path, body);
  }, [request]);

  const put = useCallback(async (path, body) => {
    return request('put', path, body);
  }, [request]);

  return useMemo(() => ({
    request,
    get,
    post,
    put
  }), [
    request,
    get,
    post,
    put
  ]);
};

export default useApiEndpoint;
